import { ErrorBoundary, LoadingState } from '@ibe/ui';
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { LoginPage } from './auth/login-page.js';
import { RequireAuth } from './auth/permission-guard.js';
import { AppLayout } from './layout/app-layout.js';
import { amenityRoutes } from './modules/amenities/index.js';
import { guestRoutes } from './modules/guests/index.js';
import { inclusionRoutes } from './modules/inclusions/index.js';
import { pmsCredentialRoutes } from './modules/pms-credentials/index.js';
import { propertyGroupRoutes } from './modules/property-groups/index.js';

import { propertyRoutes } from './modules/properties/index.js';
import { promotionRoutes } from './modules/promotions/index.js';
import { roomTypeRoutes } from './modules/room-types/index.js';
import { reservationRoutes } from './modules/reservations/index.js';
import { tenantAdminRoutes } from './modules/tenant-admins/index.js';
import { reservationSummaryRoutes } from './modules/reservation-summary/index.js';
import { propertySetupRoutes } from './modules/property-setup/index.js';
import { cancellationPolicyRoutes } from './modules/cancellation-policies/index.js';
import { notificationGatewayRoutes } from './modules/notification-gateways/index.js';
import { notificationTemplateRoutes } from './modules/notification-templates/index.js';
import { paymentGatewayRoutes } from './modules/payment-gateways/index.js';
import { paymentPolicyRoutes } from './modules/payment-policies/index.js';
import { ratePlanRoutes } from './modules/rate-plans/index.js';
import { dailyRateRoutes } from './modules/daily-rates/index.js';
import { exchangeRateRoutes } from './modules/exchange-rates/index.js';
import { taxRoutes } from './modules/taxes/index.js';
import { roleRoutes } from './modules/roles/index.js';
import { tenantRoutes } from './modules/tenants/index.js';
import { userRoutes } from './modules/users/index.js';
import { referenceRoutes } from './modules/reference/index.js';
import { ComingSoonPage } from './pages/coming-soon-page.js';
import { NotFoundPage } from './pages/not-found-page.js';

/**
 * Screens are loaded when they are first opened.
 *
 * Not eagerly, because the bundle had reached 499 kB against a 500 kB budget —
 * under by less than a kilobyte, which is a budget that gets breached by
 * whoever adds the next component and has nothing to do with them. Splitting
 * by route is the natural seam: somebody signing in needs the login form, not
 * the availability calendar, the rules engine and every detail screen.
 *
 * The login page and the layout stay eager. They are on the path to every
 * other screen, so deferring them buys nothing and costs a flash of a spinner
 * before the application has drawn anything at all.
 */
const DashboardPage = lazy(async () => ({
  default: (await import('./pages/dashboard-page.js')).DashboardPage,
}));
const AuditPage = lazy(async () => ({ default: (await import('./pages/lists.js')).AuditPage }));
const RoomTypesPage = lazy(async () => ({
  default: (await import('./pages/rooms.js')).RoomTypesPage,
}));
const RoomTypeDetailPage = lazy(async () => ({
  default: (await import('./pages/rooms.js')).RoomTypeDetailPage,
}));
const RoomsPage = lazy(async () => ({ default: (await import('./pages/rooms.js')).RoomsPage }));
const PropertyImagesPage = lazy(async () => ({
  default: (await import('./pages/property-images.js')).PropertyImagesPage,
}));
const RoomImagesPage = lazy(async () => ({
  default: (await import('./pages/room-images.js')).RoomImagesPage,
}));
const AvailabilityCalendarPage = lazy(async () => ({
  default: (await import('./pages/availability.js')).AvailabilityCalendarPage,
}));
const AvailabilityPage = lazy(async () => ({
  default: (await import('./modules/availability/pages/availability-page.js')).AvailabilityPage,
}));

const RulesPage = lazy(async () => ({ default: (await import('./pages/rules.js')).RulesPage }));
const TenantDomainsPage = lazy(async () => ({
  default: (await import('./pages/tenant-domains.js')).TenantDomainsPage,
}));
const ForgotPasswordPage = lazy(async () => ({
  default: (await import('./auth/forgot-password-page.js')).ForgotPasswordPage,
}));
const VerifyOtpPage = lazy(async () => ({
  default: (await import('./auth/verify-otp-page.js')).VerifyOtpPage,
}));
const ResetPasswordPage = lazy(async () => ({
  default: (await import('./auth/reset-password-page.js')).ResetPasswordPage,
}));

function RoutedTenantDomainsPage(): React.JSX.Element {
  const { tenantId = '' } = useParams();
  return <TenantDomainsPage tenantId={tenantId} />;
}

/**
 * The route tree.
 *
 * The error boundary sits **inside** the layout rather than around it, and its
 * reset key is the pathname. Inside, because a page that throws should leave
 * the sidebar and the sign-out button working — a boundary around the whole
 * shell turns one broken page into an application with no way out. Keyed on
 * the path, because React does not remount a boundary when its children
 * change, so without a key the fallback would still be there after navigating
 * somewhere that works.
 */
export function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/forgot-password"
        element={
          <Boundary>
            <ForgotPasswordPage />
          </Boundary>
        }
      />
      <Route
        path="/verify-otp"
        element={
          <Boundary>
            <VerifyOtpPage />
          </Boundary>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Boundary>
            <ResetPasswordPage />
          </Boundary>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <Boundary>
              <DashboardPage />
            </Boundary>
          }
        />
        {propertyRoutes()}
        {roomTypeRoutes()}
        {pmsCredentialRoutes()}
        {amenityRoutes()}
        {inclusionRoutes()}
        {propertyGroupRoutes()}
        {cancellationPolicyRoutes()}
        {paymentPolicyRoutes()}
        {ratePlanRoutes()}
        {dailyRateRoutes()}
        {taxRoutes()}
        {exchangeRateRoutes()}
        {promotionRoutes()}
        <Route
          path="/property-images"
          element={
            <Boundary>
              <PropertyImagesPage />
            </Boundary>
          }
        />
        {propertySetupRoutes()}
        <Route
          path="/room-images"
          element={
            <Boundary>
              <RoomImagesPage />
            </Boundary>
          }
        />
        {reservationRoutes()}
        {reservationSummaryRoutes()}
        {guestRoutes()}
        {userRoutes()}
        {roleRoutes()}
        <Route
          path="/audit"
          element={
            <Boundary>
              <AuditPage />
            </Boundary>
          }
        />

        {paymentGatewayRoutes()}
        {notificationGatewayRoutes()}
        {notificationTemplateRoutes()}
        <Route
          path="/availability"
          element={
            <Boundary>
              <AvailabilityPage />
            </Boundary>
          }
        />
        <Route
          path="/reports"
          element={
            <Boundary>
              <ComingSoonPage
                title="Reports"
                description="Occupancy, revenue and booking performance across your properties."
              />
            </Boundary>
          }
        />

        <Route
          path="/permissions"
          element={
            <Boundary>
              <ComingSoonPage
                title="Permissions"
                description="The permissions each role can grant, and what they allow."
              />
            </Boundary>
          }
        />
        <Route
          path="/features"
          element={
            <Boundary>
              <ComingSoonPage
                title="Features"
                description="Amenities and facilities that can be attached to a property or room type."
              />
            </Boundary>
          }
        />
        <Route
          path="/inclusions"
          element={
            <Boundary>
              <ComingSoonPage
                title="Inclusions"
                description="What a rate includes — breakfast, transfers, and the rest."
              />
            </Boundary>
          }
        />
        <Route
          path="/oauth-providers"
          element={
            <Boundary>
              <ComingSoonPage
                title="OAuth providers"
                description="Sign-in providers your staff can authenticate through."
              />
            </Boundary>
          }
        />
        <Route
          path="/city-images"
          element={
            <Boundary>
              <ComingSoonPage
                title="City images"
                description="Imagery shown for each city across the booking experience."
              />
            </Boundary>
          }
        />
        <Route
          path="/support"
          element={
            <Boundary>
              <ComingSoonPage
                title="Support and FAQ"
                description="Help articles and the answers your staff ask for most."
              />
            </Boundary>
          }
        />

        {/* Nested under the property in the URL even though the API takes it
            as a query parameter. A room number means nothing without knowing
            whose it is, and the breadcrumb has to lead somewhere. */}
        <Route
          path="/properties/:propertyId/room-types"
          element={
            <Boundary>
              <RoomTypesPage />
            </Boundary>
          }
        />
        <Route
          path="/properties/:propertyId/room-types/:roomTypeId"
          element={
            <Boundary>
              <RoomTypeDetailPage />
            </Boundary>
          }
        />
        <Route
          path="/properties/:propertyId/rooms"
          element={
            <Boundary>
              <RoomsPage />
            </Boundary>
          }
        />
        <Route
          path="/properties/:propertyId/availability"
          element={
            <Boundary>
              <AvailabilityCalendarPage />
            </Boundary>
          }
        />

        <Route
          path="/properties/:propertyId/rules"
          element={
            <Boundary>
              <RulesPage />
            </Boundary>
          }
        />
        {tenantRoutes()}
        {tenantAdminRoutes()}
        {referenceRoutes()}
        <Route
          path="/tenants/:tenantId/domains"
          element={
            <Boundary>
              <RoutedTenantDomainsPage />
            </Boundary>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/**
 * One wrapper per route: an error boundary and a suspense fallback.
 *
 * Suspense sits *inside* the boundary rather than around it, so a chunk that
 * fails to load — a deploy that replaced the file mid-session is the usual
 * cause — surfaces as the boundary's error rather than as a spinner that never
 * resolves. A permanent spinner is the worst of the three outcomes: nothing
 * tells the user to reload, and nothing tells us either.
 */
function Boundary({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const location = useLocation();

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingState label="Loading" />}>{children}</Suspense>
    </ErrorBoundary>
  );
}
